import React, { createContext, useContext, useState, useEffect } from "react";
import { Alert, AlertProps, Fade, Slide, SlideProps, Snackbar, Button } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

// Create a type for the context value
interface SnackbarContextType {
  setErrorMessage: (message: string | string[], severity?: AlertProps["severity"], action?: SnackbarAction) => void;
  messages?: string | string[];
}

interface SnackbarAction {
  label: string;
  onClick: () => void;
}

// Create the context
const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

// Helper transition function
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

type TransitionType = React.ComponentType<
TransitionProps & {
  children: React.ReactElement<unknown, string>;
}
>;

// SnackbarProvider that manages the state and provides context value
export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [severity, setSeverity] = useState<AlertProps["severity"]>("error");
  const [state, setState] = useState<{
    open: boolean;
    Transition: TransitionType;
  }>({
    open: false,
    Transition: Fade,
  });
  const [autoHideDuration, setAutoHideDuration] = useState(5000);

  const [messages, setMessages] = useState<string[]>([]);
  const [action, setAction] = useState<SnackbarAction | undefined>(undefined);

  const showSnackbar = (
    Transition: TransitionType
  ) => {
    setState({
      open: true,
      Transition,
    });
  };

  const handleClose = () => {
    setState((prevState) => ({
      ...prevState,
      open: false,
    }));
    // Clear action when closing
    setAction(undefined);
  };

  const setErrorMessage = (message: string | string[], s?: AlertProps["severity"], a?: SnackbarAction) => {
    // Allow setting a single message or an array of messages
    setMessages(Array.isArray(message) ? message : [message]);
    setSeverity(s ?? 'error');
    setAction(a);

    // Set longer duration for info messages (like admission notifications)
    setAutoHideDuration(s === 'info' ? 10000 : 5000);
  };

  useEffect(() => {
    if (messages.length > 0) {
      showSnackbar(SlideTransition);
    }
  }, [messages]);

  return (
    <SnackbarContext.Provider value={{ messages, setErrorMessage }}>
      {children}
      <Snackbar
        open={state.open}
        onClose={handleClose}
        TransitionComponent={state.Transition}
        key={state.Transition.name}
        autoHideDuration={autoHideDuration}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '1rem',
            fontWeight: 600,
            minWidth: 360,
          }}
          action={
            action ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  action.onClick();
                  handleClose();
                }}
                sx={{
                  fontWeight: 700,
                  textDecoration: 'underline',
                }}
              >
                {action.label}
              </Button>
            ) : undefined
          }
        >
          {messages.join(", ")}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

// Custom hook to use the context
export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
